import { useLocation } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalendarDays, FileText, Instagram, MessageCircle, PlugZap, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";

const integrations = [
  { title: "Instagram", description: "Centralize mensagens e permissões da conta comercial conectada.", icon: Instagram, action: "/instagram", availability: "Módulo disponível", requestChannel: "instagram" as const },
  { title: "WhatsApp e automações", description: "Acompanhe mensagens, auditoria e falhas operacionais do canal.", icon: MessageCircle, action: "/automation-monitor", availability: "Módulo disponível", requestChannel: "whatsapp" as const },
  { title: "Fiscal", description: "Acesse a gestão de notas e os dados necessários para a operação fiscal.", icon: FileText, action: "/fiscal", availability: "Módulo disponível" },
  { title: "Calendário externo", description: "A conexão com agendas externas ainda está em planejamento e não compartilha dados do salão.", icon: CalendarDays, availability: "Em planejamento", isPlanned: true },
];

const CHANNEL_REQUEST_STATUS: Record<string, { label: string; className: string }> = {
  pending: { label: "Aguardando equipe", className: "bg-amber-500/15 text-amber-700 border-amber-500/30" },
  in_progress: { label: "Em andamento", className: "bg-blue-500/15 text-blue-700 border-blue-500/30" },
  done: { label: "Concluído", className: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30" },
  cancelled: { label: "Cancelado", className: "bg-red-500/15 text-red-700 border-red-500/30" },
};

const CHANNEL_REQUEST_GUIDANCE: Record<string, string> = {
  pending: "A equipe recebeu sua solicitação. Você será orientado(a) antes de qualquer ativação.",
  in_progress: "A conexão está sendo preparada. Mantenha os dados do canal disponíveis caso a equipe precise confirmar alguma informação.",
  done: "Conexão concluída. Abra a configuração do canal para acompanhar a operação.",
  cancelled: "A solicitação foi encerrada. Consulte a orientação da equipe antes de enviar uma nova solicitação.",
};

export default function AdminIntegrations() {
  const [, navigate] = useLocation();
  const utils = trpc.useUtils();
  const { data: channelRequests } = trpc.salon.getChannelRequests.useQuery();
  const requestConnection = trpc.salon.requestChannelConnection.useMutation({
    onSuccess: (result, variables) => {
      const channel = variables.channelType === "whatsapp" ? "WhatsApp" : "Instagram";
      utils.salon.getChannelRequests.invalidate();
      toast.success(`Solicitação de ${channel} enviada`, {
        description: `Solicitação #${result.requestId} registrada. Acompanhe o andamento nesta Central.`,
      });
    },
    onError: (error) => toast.error("Não foi possível solicitar a conexão", { description: error.message }),
  });

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><PlugZap className="h-5 w-5" /></span>
        <div><h1 className="text-2xl font-bold tracking-tight">Central de Integrações</h1><p className="mt-1 text-sm text-muted-foreground">Organize os conectores do salão e acesse cada configuração em um único lugar.</p></div>
      </div>
      <Card className="border-primary/20 bg-primary/5"><CardContent className="flex gap-3 p-4"><ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" /><p className="text-sm text-muted-foreground">Credenciais e permissões continuam protegidas nas configurações de cada integração. Nenhum canal é ativado automaticamente.</p></CardContent></Card>
      <div className="grid gap-4 md:grid-cols-2">
        {integrations.map((integration) => {
          const Icon = integration.icon;
          const latestRequest = integration.requestChannel
            ? channelRequests?.find((request: any) => request.channelType === integration.requestChannel)
            : null;
          const requestStatus = latestRequest ? CHANNEL_REQUEST_STATUS[latestRequest.status] ?? CHANNEL_REQUEST_STATUS.pending : null;
          const requestOpen = latestRequest?.status === "pending" || latestRequest?.status === "in_progress";
          const requestDate = latestRequest?.updatedAt ?? latestRequest?.createdAt;

          return (
            <Card key={integration.title} className="flex flex-col">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted text-primary"><Icon className="h-5 w-5" /></span>
                  {requestStatus ? <Badge className={requestStatus.className}>{requestStatus.label}</Badge> : <Badge variant="outline">{integration.availability}</Badge>}
                </div>
                <CardTitle className="pt-3 text-base">{integration.title}</CardTitle>
                <CardDescription>{integration.description}</CardDescription>
              </CardHeader>
              <CardContent className="mt-auto space-y-2">
                {latestRequest && (
                  <div className="rounded-lg border border-border/60 bg-muted/35 p-3 text-xs">
                    <p className="font-medium text-foreground">Solicitação #{latestRequest.id}</p>
                    <p className="mt-1 text-muted-foreground">{requestDate ? `Atualizada em ${new Date(requestDate).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })}` : "Data de atualização indisponível"}</p>
                    <p className="mt-2 border-t border-border/60 pt-2 text-muted-foreground"><strong className="text-foreground">Próximo passo:</strong> {CHANNEL_REQUEST_GUIDANCE[latestRequest.status] ?? CHANNEL_REQUEST_GUIDANCE.pending}</p>
                    {latestRequest.adminNotes && <p className="mt-2 border-t border-border/60 pt-2 text-muted-foreground"><strong className="text-foreground">Orientação da equipe:</strong> {latestRequest.adminNotes}</p>}
                    {latestRequest.adminContactPhone && <p className="mt-1 text-muted-foreground">Contato da equipe: {latestRequest.adminContactPhone}</p>}
                  </div>
                )}
                {integration.isPlanned ? (
                  <p className="rounded-lg border border-dashed border-border bg-muted/30 px-3 py-2 text-center text-xs text-muted-foreground">
                    Conector ainda não disponível
                  </p>
                ) : (
                  <Button variant="outline" className="w-full" onClick={() => navigate(integration.action!)}>Abrir configuração</Button>
                )}
                {integration.requestChannel && (
                  <Button
                    className="w-full"
                    variant="secondary"
                    disabled={requestConnection.isPending || requestOpen}
                    onClick={() => requestConnection.mutate({ channelType: integration.requestChannel })}
                  >
                    {requestConnection.isPending ? "Enviando solicitação..." : requestOpen ? "Solicitação em andamento" : latestRequest ? "Solicitar nova conexão" : "Solicitar conexão"}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
